import React, { useState } from 'react';
import {
    Grid, Paper, Tabs, Tab, Box, Typography, Button, IconButton, Divider,
    Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import PageHeader from '../../../../../components/page-header';
import { usePublicAmendmentDetailController } from './context/public-amendment-detail-controller';
import { usePublicAmendmentDetailState } from './context/public-amendment-detail-context';
import { getAmendmentTypeLabel, getAmendmentStatus } from '../../../../../model/sge/amendment/amendment-enums';
import { getAccountReportStatus } from '../../../../../model/sge/account-report/account-report-enums';
import { getLegalInstrumentTypeLabel } from '../../../../../model/sge/transfer/transfer-enums';
import { formatDocument } from '../../../../../utils/document';
import { usePublicPortalBasePath } from '../../../context/public-client-context';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
    return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function EmptyRow({ colSpan, message }: { colSpan: number; message: string }) {
    return (
        <TableRow>
            <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: 'text.secondary', border: 'none' }}>
                {message}
            </TableCell>
        </TableRow>
    );
}

function InfoField({ label, value }: { label: string; value?: React.ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2" sx={{ mt: 0.25 }}>{value || '-'}</Typography>
        </Box>
    );
}

export function PublicAmendmentDetailComponent() {
    const state = usePublicAmendmentDetailState();
    const ctrl = usePublicAmendmentDetailController();
    const basePath = usePublicPortalBasePath();
    const [expandedLiquidation, setExpandedLiquidation] = useState<number | null>(null);

    const fmtCurrency = (v?: number) => v != null ? `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-';
    const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';

    const statusInfo = getAmendmentStatus(state.amendment?.status);

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title={state.amendment?.name || 'Emenda Parlamentar'}
                    subtitle={`Exercício ${state.amendment?.year || '-'} • ${getAmendmentTypeLabel(state.amendment?.type)}`}
                    actions={<Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={ctrl.navigateToList}>Voltar</Button>}
                    homeHref={basePath}
                    breadcrumbs={[{ label: 'Emendas', href: `${basePath}/emendas` }, { label: 'Detalhe' }]}
                />
            </Grid>

            {/* Parlamentar e Partido */}
            <Grid item xs={12}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText',
                        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3,
                    }}
                >
                    <Box sx={{
                        width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: 'rgba(255,255,255,0.15)', flexShrink: 0,
                    }}>
                        <PersonIcon />
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Parlamentar Autor
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {state.parlamentarName || 'Não informado'}
                        </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                    <Box>
                        <Typography variant="caption" sx={{ opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Partido Político
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {state.politicalPartyAbbreviation || 'Não informado'}
                        </Typography>
                    </Box>
                </Paper>
            </Grid>

            {/* Summary */}
            <Grid item xs={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <Grid container spacing={3}>

                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                                Informações Gerais
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Box sx={{ mt: 0.5 }}>
                                <Chip label={statusInfo?.label || '-'} color={statusInfo?.color || 'default'} size="small" />
                            </Box>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <InfoField label="Valor Total" value={<Typography variant="body1" sx={{ fontWeight: 600 }}>{fmtCurrency(state.amendment?.totalAmount)}</Typography>} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <InfoField label="Exercício" value={state.amendment?.year} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <InfoField label="Tipo" value={getAmendmentTypeLabel(state.amendment?.type)} />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Classificação Orçamentária
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InfoField label="Unidade Orçamentária" value={state.amendment?.budgetUnitName ? `${state.amendment.budgetUnitCode} - ${state.amendment.budgetUnitName}` : undefined} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InfoField label="Programa Orçamentário" value={state.amendment?.budgetProgramName ? `${state.amendment.budgetProgramCode} - ${state.amendment.budgetProgramName}` : undefined} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InfoField label="Ação Orçamentária" value={state.amendment?.budgetActionName ? `${state.amendment.budgetActionCode} - ${state.amendment.budgetActionName}` : undefined} />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Detalhes
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12}>
                            <InfoField label="Descrição do Objeto" value={state.amendment?.objectDescription} />
                        </Grid>
                        <Grid item xs={12}>
                            <InfoField label="Base Legal" value={state.amendment?.legalBasis} />
                        </Grid>
                        <Grid item xs={12}>
                            <InfoField label="Finalidade Pública" value={state.amendment?.publicPurpose} />
                        </Grid>
                        <Grid item xs={12}>
                            <InfoField label="Justificativa Institucional" value={state.amendment?.institutionalJustification} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InfoField label="Público-Alvo" value={state.amendment?.targetAudience} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InfoField label="Local de Execução" value={state.amendment?.executionLocation} />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Tabs */}
            <Grid item xs={12}>
                <Paper elevation={3}>
                    <Tabs value={state.tabIndex} onChange={ctrl.handleTabChange} variant="scrollable" scrollButtons="auto">
                        <Tab icon={<ReceiptLongIcon fontSize="small" />} iconPosition="start" label={`Empenhos (${state.commitments.length})`} />
                        <Tab icon={<FactCheckIcon fontSize="small" />} iconPosition="start" label={`Prestação de Contas (${state.accountReports.length})`} />
                        <Tab icon={<AttachFileIcon fontSize="small" />} iconPosition="start" label={`Anexos (${state.attachments.length})`} />
                        <Tab icon={<SwapHorizIcon fontSize="small" />} iconPosition="start" label={`Transferências (${state.transfers.length})`} />
                    </Tabs>
                    <Box sx={{ p: 2 }}>

                        {/* TAB 0: Commitments */}
                        <TabPanel value={state.tabIndex} index={0}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Clique em um empenho para ver suas liquidações
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width={40} />
                                            <TableCell>Nº Empenho</TableCell>
                                            <TableCell>Data</TableCell>
                                            <TableCell>Fornecedor</TableCell>
                                            <TableCell>Documento</TableCell>
                                            <TableCell align="right">Valor Empenhado</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {state.commitments.length === 0 && <EmptyRow colSpan={6} message="Nenhum empenho cadastrado" />}
                                        {state.commitments.map((c) => (
                                            <React.Fragment key={c.id}>
                                                <TableRow hover selected={state.selectedCommitment?.id === c.id} sx={{ cursor: 'pointer' }} onClick={() => ctrl.handleSelectCommitment(c)}>
                                                    <TableCell>
                                                        <IconButton size="small">
                                                            {state.selectedCommitment?.id === c.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell>{c.commitmentNumber || '-'}</TableCell>
                                                    <TableCell>{fmtDate(c.commitmentDate)}</TableCell>
                                                    <TableCell>{c.supplierName || '-'}</TableCell>
                                                    <TableCell>{formatDocument(c.supplierDocument) || '-'}</TableCell>
                                                    <TableCell align="right">{fmtCurrency(c.committedAmount)}</TableCell>
                                                </TableRow>
                                                {state.selectedCommitment?.id === c.id && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} sx={{ pl: 4, bgcolor: 'action.hover' }}>
                                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Liquidações</Typography>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>Nº Liquidação</TableCell>
                                                                        <TableCell>Data</TableCell>
                                                                        <TableCell align="right">Valor Liquidado</TableCell>
                                                                        <TableCell>Observações</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {state.liquidations.length === 0 && <EmptyRow colSpan={4} message="Nenhuma liquidação registrada" />}
                                                                    {state.liquidations.map((l) => (
                                                                        <React.Fragment key={l.id}>
                                                                            <TableRow
                                                                                hover
                                                                                selected={expandedLiquidation === l.id}
                                                                                sx={{ cursor: 'pointer' }}
                                                                                onClick={() => { setExpandedLiquidation(expandedLiquidation === l.id ? null : l.id); if (expandedLiquidation !== l.id) ctrl.loadPayments(l.id); }}
                                                                            >
                                                                                <TableCell>{l.liquidationNumber || '-'}</TableCell>
                                                                                <TableCell>{fmtDate(l.liquidationDate)}</TableCell>
                                                                                <TableCell align="right">{fmtCurrency(l.liquidatedAmount)}</TableCell>
                                                                                <TableCell>{l.observations || '-'}</TableCell>
                                                                            </TableRow>
                                                                            {expandedLiquidation === l.id && (
                                                                                <TableRow>
                                                                                    <TableCell colSpan={4} sx={{ pl: 8, bgcolor: 'background.default' }}>
                                                                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Pagamentos</Typography>
                                                                                        <Table size="small">
                                                                                            <TableHead><TableRow><TableCell>Nº Ordem</TableCell><TableCell>Data</TableCell><TableCell align="right">Valor Pago</TableCell></TableRow></TableHead>
                                                                                            <TableBody>
                                                                                                {state.payments.length === 0 && <EmptyRow colSpan={3} message="Nenhum pagamento registrado" />}
                                                                                                {state.payments.map((p) => (
                                                                                                    <TableRow key={p.id}>
                                                                                                        <TableCell>{p.paymentOrderNumber || '-'}</TableCell>
                                                                                                        <TableCell>{fmtDate(p.paymentDate)}</TableCell>
                                                                                                        <TableCell align="right">{fmtCurrency(p.paidAmount)}</TableCell>
                                                                                                    </TableRow>
                                                                                                ))}
                                                                                            </TableBody>
                                                                                        </Table>
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            )}
                                                                        </React.Fragment>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        {/* TAB 1: Account Reports */}
                        <TabPanel value={state.tabIndex} index={1}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead><TableRow><TableCell>Período</TableCell><TableCell>Status</TableCell><TableCell>Observações</TableCell></TableRow></TableHead>
                                    <TableBody>
                                        {state.accountReports.length === 0 && <EmptyRow colSpan={3} message="Nenhuma prestação de contas cadastrada" />}
                                        {state.accountReports.map((r) => (
                                            <TableRow key={r.id} hover>
                                                <TableCell>{fmtDate(r.periodStartDate)} - {fmtDate(r.periodEndDate)}</TableCell>
                                                <TableCell>
                                                    {getAccountReportStatus(r.status) ? (
                                                        <Chip label={getAccountReportStatus(r.status)?.label} color={getAccountReportStatus(r.status)?.color} size="small" />
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell>{r.observations || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        {/* TAB 2: Attachments */}
                        <TabPanel value={state.tabIndex} index={2}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead><TableRow><TableCell>Nome</TableCell><TableCell>Tipo</TableCell><TableCell>Data</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead>
                                    <TableBody>
                                        {state.attachments.length === 0 && <EmptyRow colSpan={4} message="Nenhum anexo disponível" />}
                                        {state.attachments.map((a) => (
                                            <TableRow key={a.id} hover>
                                                <TableCell>{a.title || a.originalFileName}</TableCell>
                                                <TableCell>{a.fileType}</TableCell>
                                                <TableCell>{fmtDate(a.createdAt)}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={() => ctrl.handleDownloadAttachment(a.id, a.originalFileName || 'arquivo')}><DownloadIcon fontSize="small" /></IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        {/* TAB 3: Transfers */}
                        <TabPanel value={state.tabIndex} index={3}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead><TableRow><TableCell>Empresa</TableCell><TableCell>CNPJ</TableCell><TableCell>Tipo Instrumento</TableCell><TableCell>Nº Instrumento</TableCell><TableCell>Data Assinatura</TableCell><TableCell align="right">Valor</TableCell></TableRow></TableHead>
                                    <TableBody>
                                        {state.transfers.length === 0 && <EmptyRow colSpan={6} message="Nenhuma transferência cadastrada" />}
                                        {state.transfers.map((t) => (
                                            <TableRow key={t.id} hover>
                                                <TableCell>{t.companyName || '-'}</TableCell>
                                                <TableCell>{formatDocument(t.cnpj) || '-'}</TableCell>
                                                <TableCell>{getLegalInstrumentTypeLabel(t.legalInstrumentType)}</TableCell>
                                                <TableCell>{t.instrumentNumber || '-'}</TableCell>
                                                <TableCell>{fmtDate(t.signatureDate)}</TableCell>
                                                <TableCell align="right">{fmtCurrency(t.transferredAmount)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>
                    </Box>
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
